import { useEffect, useState } from 'react';
import { keyBy } from 'lodash';

import { Box, Stack, Typography } from '@mui/material';
import ReactPlayer from 'react-player';

import { IViewUserVideoInterviewData } from 'src/types/userVideoInterviewData';
import { Loader } from 'src/components';
import { useAppDispatch, useAppSelector } from 'src/redux';
import {
  getViewUserVideoInterviewViewUrl,
  videoInterviewSelectors,
} from 'src/redux/videoInterview';
import { isStatusLoading } from 'src/utils';

type VideoInterviewQuestionsProps = {
  token: string;
  userVideoInterviewData: IViewUserVideoInterviewData;
};

export const VideoInterviewQuestions = ({
  token,
  userVideoInterviewData,
}: VideoInterviewQuestionsProps) => {
  const dispatch = useAppDispatch();
  const {
    selectedVideoInterviewViewUrlApiStatus,
    selectedVideoInterviewViewUrl,
  } = useAppSelector(videoInterviewSelectors.getUserVideoInterviewViewAPIData);
  const [selectedQuestion, setSelectedQuestion] = useState<number | null>(1);

  const questions = userVideoInterviewData.videoInterview?.questions ?? [];

  const recordByQuestionNumber = keyBy(
    userVideoInterviewData.records ?? [],
    'questionNumber',
  );

  const fileName = selectedQuestion
    ? recordByQuestionNumber[selectedQuestion]?.fileName
    : recordByQuestionNumber[1]?.fileName;

  useEffect(() => {
    if (fileName) {
      dispatch(
        getViewUserVideoInterviewViewUrl({
          token,
          fileName,
        }),
      );
    }
  }, [fileName]);

  const isLoading = isStatusLoading(selectedVideoInterviewViewUrlApiStatus);

  return (
    <Stack direction={{ xs: 'column', md: 'row' }}>
      <Stack direction="row" gap={3} justifyContent="center">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            background: 'black',
            minWidth: '530px',
            borderRadius: '4px',
          }}
        >
          {selectedVideoInterviewViewUrl ? (
            <Box position="relative" width="100%" height="100%">
              {isLoading && <Loader zIndex={2} />}
              <ReactPlayer
                controls
                playing
                muted
                width="100%"
                height="100%"
                url={selectedVideoInterviewViewUrl ?? ''}
                style={{
                  background: 'black',
                  borderRadius: '4px',
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2" color="text.invertedSecondary">
              Please select a question to play
            </Typography>
          )}
        </Box>
        <Stack gap={0.5} sx={{ width: '50%' }}>
          <Typography variant="h1" mb="1rem">
            {userVideoInterviewData.user?.firstName}{' '}
            {userVideoInterviewData.user?.lastName} video introduction
          </Typography>
          {questions.map((question) => {
            const hasRecord = !!recordByQuestionNumber[question.questionNumber];
            return (
              <Box
                key={question.questionNumber}
                onClick={() =>
                  hasRecord && setSelectedQuestion(question.questionNumber)
                }
                sx={(theme) => ({
                  padding: '8px 16px 16px 16px',
                  cursor: hasRecord ? 'pointer' : 'default',
                  borderRadius: '4px',
                  background:
                    selectedQuestion === question.questionNumber
                      ? theme.palette.highlight.accent
                      : hasRecord
                      ? theme.palette.highlight.actionable
                      : theme.palette.highlight.neutral,
                })}
              >
                <Typography variant="body2" color="text.secondary">
                  Question {question.questionNumber}
                </Typography>
                <Typography variant="body1">{question.title}</Typography>
              </Box>
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
};
